import CameraAltIcon from "@mui/icons-material/CameraAlt";
import Stack from "@mui/material/Stack";
import { CircularProgressWithLabel } from "core/components/CircularProgressWithLabel";
import {
  ReactHookFileInputNew,
  type ReactHookFileInputNewProps,
} from "core/components/ReactHookFormComponents/ReactHookFileInputNew";
import { useToaster } from "core/hooks/useToaster";
import { checkCaptureEnvironmentSupport } from "core/utils/checkCaptureEnvironmentSupport";
import { exhaustiveCheck } from "core/utils/exhaustiveCheck";
import type { EstimateImageLocation } from "features/EstimateRequest/entities";
import { type ReactNode, useState } from "react";
import {
  type Control,
  type FieldPath,
  type FieldValues,
  useController,
} from "react-hook-form";
import { useTranslation } from "react-i18next";
import { inputAcceptImages } from "../estimateRequestFormPageDefs";
import { useEstimateRequestImageUpload } from "../hooks/useEstimateRequestImageUpload";
import { FileImagePreview } from "./FileImagePreview";

const WIDTH = 175 as const;

type EstimateRequestImageUploadProps<TFieldValues extends FieldValues> = {
  control: Control<TFieldValues>;
  fileFieldName: FieldPath<TFieldValues>;
  fileNameFieldName: FieldPath<TFieldValues>;
  fileIdFieldName: FieldPath<TFieldValues>;
  imagePlaceholderSlot?: ReactNode;
  shopId: string;
  location: EstimateImageLocation;
  estimateRequestId: string;
  imageNumber: number;
  uploadLabel?: string;
  cameraUploadLabel?: string;
  required?: boolean;
  onSuccess?: () => void;
  onCancelClick?: () => void;
};

export function EstimateRequestImageUpload<TFieldValues extends FieldValues>({
  control,
  fileFieldName,
  fileNameFieldName,
  fileIdFieldName,
  estimateRequestId,
  imagePlaceholderSlot,
  location,
  shopId,
  imageNumber,
  uploadLabel,
  cameraUploadLabel,
  required = true,
  onSuccess,
  onCancelClick,
}: EstimateRequestImageUploadProps<TFieldValues>) {
  const uploadImage = useEstimateRequestImageUpload();
  const [loadingProgress, setLoadingProgress] = useState<number | null>(null);
  const {
    field: { value: file },
    fieldState: { error: fileError },
  } = useController({ control, name: fileFieldName });
  const {
    field: { value: fileName },
  } = useController({ control, name: fileNameFieldName });
  const {
    field: { onChange: setFileId },
  } = useController({ control, name: fileIdFieldName });
  const { t } = useTranslation("Fixhub", {
    keyPrefix: "Next:Core:EstimateRequestImageUpload",
  });
  const { errorToast } = useToaster();
  const defaultUploadLabel = t("upload");
  const defaultCameraUploadLabel = t("camera");
  const failedToPutToBlobStorage = t("failedToPutToBlobStorage");
  const failedToGetUploadToken = t("failedToGetUploadToken");
  const failedToConfirmUpload = t("failedToConfirmUpload");
  const recaptchaNotLoaded = t("recaptchaNotLoaded");
  const recaptchaNotGenerated = t("recaptchaNotGenerated");
  const onFileReceived: ReactHookFileInputNewProps["onFileReceived"] = async (
    uploadFile
  ) => {
    const result = await uploadImage(
      uploadFile,
      {
        estimateRequestId,
        imageLocation: location,
        imageNumber,
        shopId,
      },
      setLoadingProgress
    );

    if (!result.ok) {
      switch (result.errorCode) {
        case "NO_FILE_SELECTED":
          break;
        case "FETCH_UPLOAD_TOKEN_FAILED":
          errorToast(failedToGetUploadToken);
          break;
        case "CONFIRM_UPLOAD_FAILED":
          errorToast(failedToConfirmUpload);
          break;
        case "BLOB_UPLOAD_FAILED":
          errorToast(failedToPutToBlobStorage);
          break;
        case "RECAPTCHA_NOT_LOADED":
          errorToast(recaptchaNotLoaded);
          break;
        case "RECAPTCHA_NOT_GENERATED":
          errorToast(recaptchaNotGenerated);
          break;
        default: {
          exhaustiveCheck(result.errorCode);
        }
      }
      setFileId("");
      return { nameValue: "", fileNameValue: "" };
    }

    setFileId(result.fileId);
    onSuccess?.();
    return { nameValue: result.file, fileNameValue: result.fileName };
  };
  const canCaptureEnvironment = checkCaptureEnvironmentSupport();
  return (
    <Stack gap={1} maxWidth={WIDTH}>
      <FileImagePreview
        width={WIDTH}
        file={(file as File) ?? undefined}
        fileName={(fileName as string) ?? ""}
        error={Boolean(fileError)}
        PlaceholderSlot={imagePlaceholderSlot}
      />
      <ReactHookFileInputNew
        showIcon={false}
        clearInputAfterChange
        required={required}
        control={control}
        label={uploadLabel || defaultUploadLabel}
        name={fileFieldName}
        fileNameName={fileNameFieldName}
        onFileReceived={onFileReceived}
        onCancelClick={onCancelClick}
        inputProps={{
          accept: inputAcceptImages,
        }}
        buttonProps={{ size: "small" }}
        // Hide the file name and potential error message if the environment camera is available, the file name will be displayed under the next file input
        showFileName={!canCaptureEnvironment}
        showErrorMessage={!canCaptureEnvironment}
        loadingIndicator={
          <CircularProgressWithLabel
            color="inherit"
            variant="determinate"
            value={loadingProgress ?? 0}
            size={24}
          />
        }
      />
      {canCaptureEnvironment && (
        <ReactHookFileInputNew
          showIcon={false}
          clearInputAfterChange
          required={required}
          control={control}
          label={cameraUploadLabel || defaultCameraUploadLabel}
          name={fileFieldName}
          fileNameName={fileNameFieldName}
          onFileReceived={onFileReceived}
          onCancelClick={onCancelClick}
          startIcon={<CameraAltIcon />}
          inputProps={{
            accept: inputAcceptImages,
            capture: "environment",
          }}
          buttonProps={{ size: "small" }}
        />
      )}
    </Stack>
  );
}
